import type { AdminSpecialization } from '@cookspace-shared-contracts/core';

/**
 * Узел дерева специализаций — расширение AdminSpecialization собственными
 * `children`. Уровень в дереве считывается из `node.depth`, отступы в UI
 * рассчитываются по этому полю.
 */
export interface SpecializationTreeNode extends AdminSpecialization {
    children: SpecializationTreeNode[];
}

/**
 * Преобразует плоский список узлов в массив корневых узлов с заполненными
 * `children`. Сортировка внутри каждой группы — по `sortOrder ASC`,
 * затем по `name` (на случай совпадения sortOrder).
 *
 * Гарантирует, что:
 * - Узлы без родителя становятся корневыми.
 * - Узлы со ссылкой на несуществующего родителя (в текущем срезе) попадают
 *   в корневой список — иначе бы они исчезли. UI должен подсветить их
 *   как "осиротевшие", но это вне scope утилиты.
 */
export function buildSpecializationTree(
    items: AdminSpecialization[]
): SpecializationTreeNode[] {
    const byId = new Map<string, SpecializationTreeNode>();
    items.forEach((item) => {
        byId.set(item.id, { ...item, children: [] });
    });

    const roots: SpecializationTreeNode[] = [];
    byId.forEach((node) => {
        if (node.parentId !== null && byId.has(node.parentId)) {
            const parent = byId.get(node.parentId);
            if (parent !== undefined) {
                parent.children.push(node);
            }
        } else {
            roots.push(node);
        }
    });

    const sortNodes = (arr: SpecializationTreeNode[]): void => {
        arr.sort((a, b) => {
            if (a.sortOrder !== b.sortOrder) {
                return a.sortOrder - b.sortOrder;
            }
            return a.name.localeCompare(b.name, 'ru');
        });
        arr.forEach((n) => sortNodes(n.children));
    };

    sortNodes(roots);
    return roots;
}

/**
 * Возвращает все потомки переданного узла (включая глубоких).
 * Используется для проверки циклов при перемещении узла —
 * нельзя переместить узел в его же поддерево.
 */
export function collectDescendantIds(node: SpecializationTreeNode): Set<string> {
    const acc = new Set<string>();
    const walk = (n: SpecializationTreeNode): void => {
        n.children.forEach((c) => {
            acc.add(c.id);
            walk(c);
        });
    };
    walk(node);
    return acc;
}
